import {
  useCallback,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getUserProfile } from "../../../api/auth/user";
import {
  cleanupUserSettings,
  userSettings,
} from "../../../store/slice/auth-slice";
import { getHospitalInfo } from "../../../api/settings/Settings";
import { setHospitalData } from "../../../store/slice/hospital-info-slice";
import ScreenLoading from "../../common/loading/ScreenLoading";

const ProtectedRoute = forwardRef(({ children }, ref) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Use refs to avoid dependency issues
  const token = useRef(localStorage.getItem("token"));
  const isCheckingAuth = useRef(false);

  const redirect = useCallback(() => {
    navigate("/login");
  }, [navigate]);

  /** Get Hospital Info */
  const getHospitalInfoData = useCallback(
    async (companyId) => {
      if (!companyId) return;

      try {
        const response = await getHospitalInfo(companyId);
        dispatch(setHospitalData(response?.data?.data));
      } catch (error) {
        console.error("Failed to fetch hospital info:", error);
      }
    },
    [dispatch]
  );

  // Authentication check function
  const checkAuthentication = useCallback(
    async (forceCheck = false, isManualCall = false) => {
      // Prevent multiple simultaneous auth checks
      if (isCheckingAuth.current && !forceCheck) {
        return;
      }

      // Update token ref to get latest value
      token.current = localStorage.getItem("token");

      // If already authenticated and not forcing check, skip
      if (currentUser.loggedIn && authChecked && !forceCheck) {
        setLoading(false);
        return;
      }

      isCheckingAuth.current = true;
      setLoading(true);

      try {
        // Add 3-second delay for manual calls
        if (isManualCall) {
          await new Promise((resolve) => setTimeout(resolve, 3000));
        }

        if (token.current) {
          // Check if user is already logged in with valid data
          if (
            currentUser.loggedIn &&
            currentUser.token === token.current &&
            !forceCheck
          ) {
            await getHospitalInfoData(currentUser?.companyId);
            setAuthChecked(true);
            return;
          }

          // Make API call to verify token and get user profile
          const response = await getUserProfile();
          const userInfo = {
            ...response.data,
            token: token.current,
          };

          if (userInfo.status === "Paused" && !userInfo.restricted) {
            dispatch(cleanupUserSettings());
            localStorage.removeItem("token");
            redirect();
            return;
          }

          dispatch(userSettings(userInfo));
          await getHospitalInfoData(userInfo?.company_id);
          setAuthChecked(true);
        } else {
          // No token found
          dispatch(cleanupUserSettings());
          redirect();
        }
      } catch (error) {
        console.error("Authentication check failed:", error);
        dispatch(cleanupUserSettings());
        localStorage.removeItem("token");
        redirect();
      } finally {
        setLoading(false);
        isCheckingAuth.current = false;
      }
    },
    [
      currentUser.loggedIn,
      currentUser.token,
      currentUser.companyId,
      authChecked,
      dispatch,
      redirect,
      getHospitalInfoData,
    ]
  );

  // Expose methods to parent components
  useImperativeHandle(
    ref,
    () => ({
      checkAuthentication: () => checkAuthentication(true, true), // Manual call with delay
      refreshAuth: () => checkAuthentication(true, true), // Manual call with delay
    }),
    [checkAuthentication]
  );

  // Initial authentication check
  useEffect(() => {
    checkAuthentication(); // Normal call without delay
  }, []); // Empty dependency array - only run once on mount

  // Handle token changes (e.g., from other tabs)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "token") {
        token.current = e.newValue;
        if (!e.newValue) {
          // Token was removed
          dispatch(cleanupUserSettings());
          redirect();
        } else {
          // Token was added/changed
          checkAuthentication(true); // Normal call without delay
        }
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [dispatch, redirect, checkAuthentication]);

  if (loading) {
    return <ScreenLoading />;
  }

  if (!currentUser.loggedIn || !authChecked) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
});

ProtectedRoute.displayName = "ProtectedRoute";

export default ProtectedRoute;
